import { useRef, useState, type KeyboardEvent as RKeyboardEvent, type PointerEvent as RPointerEvent, type RefObject } from "react";

/**
 * 2つの枠の境（左右または上下）。受け取った書類の AI読み取り結果の引き出し（../eigyouryoku/docs/13 §4.5）と、
 * 外注見積の詳細の画面（#198、docs/79 §13.3）が同じ動きを使う（2026-10-17 に引き出しから取り出した）。
 *   ・ドラッグ（Pointer Events）・フォーカスして ← →（↑ ↓）で step% ずつ・Home／End で端・ダブルクリックで既定
 *   ・min〜max% の間・端末に覚える（localStorage。使えない端末はその場だけ）
 *   ・ドラッグだけにしない（WCAG 2.2 SC 2.5.7）＝使う側が「原本を広く／結果を広く」などのボタンを置き、put を呼ぶ
 * 返す split は1つ目の枠（左・上）の割合（%）。描く側は CSS の変数などで幅を決める
 */
export type SplitOptions = { storageKey: string; def: number; min?: number; max?: number; step?: number };

export function useSplit({ storageKey, def, min = 30, max = 70, step = 5 }: SplitOptions) {
  const clamp = (n: number) => Math.max(min, Math.min(max, Math.round(n)));
  const [split, setSplit] = useState<number>(() => {
    try {
      const n = Number(localStorage.getItem(storageKey));
      return Number.isFinite(n) && n >= min && n <= max ? n : def;
    } catch {
      return def;
    }
  });
  const put = (n: number) => {
    const next = clamp(n);
    setSplit(next);
    try {
      localStorage.setItem(storageKey, String(next));
    } catch {
      // 覚えられない端末（プライベート・閉じたサイトのデータ）は、その場だけ
    }
  };
  const bodyRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState(false);
  /** 境の要素に付ける（vertical＝境の線が縦＝左右に並ぶ）。aria-label・className・data-testid は使う側が足す */
  const gripProps = (vertical: boolean) => {
    const fromPointer = (e: RPointerEvent<HTMLElement>) => {
      const box = bodyRef.current?.getBoundingClientRect();
      if (!box) return null;
      return vertical ? ((e.clientX - box.left) / box.width) * 100 : ((e.clientY - box.top) / box.height) * 100;
    };
    return {
      role: "separator" as const,
      tabIndex: 0,
      "aria-orientation": vertical ? ("vertical" as const) : ("horizontal" as const),
      "aria-valuenow": split,
      "aria-valuemin": min,
      "aria-valuemax": max,
      onPointerDown: (e: RPointerEvent<HTMLElement>) => {
        if (e.button !== 0) return;
        e.preventDefault();
        e.currentTarget.setPointerCapture(e.pointerId);
        setDragging(true);
      },
      onPointerMove: (e: RPointerEvent<HTMLElement>) => {
        if (!dragging) return;
        const n = fromPointer(e);
        if (n != null) setSplit(clamp(n));
      },
      onPointerUp: (e: RPointerEvent<HTMLElement>) => {
        if (!dragging) return;
        setDragging(false);
        const n = fromPointer(e);
        put(n == null ? split : n);
      },
      onPointerCancel: (e: RPointerEvent<HTMLElement>) => {
        if (!dragging) return;
        setDragging(false);
        const n = fromPointer(e);
        put(n == null ? split : n);
      },
      onDoubleClick: () => put(def),
      onKeyDown: (e: RKeyboardEvent<HTMLElement>) => {
        const dec = e.key === "ArrowLeft" || e.key === "ArrowUp";
        const inc = e.key === "ArrowRight" || e.key === "ArrowDown";
        if (dec || inc) {
          e.preventDefault();
          put(split + (inc ? step : -step));
        } else if (e.key === "Home" || e.key === "End") {
          e.preventDefault();
          put(e.key === "Home" ? min : max);
        }
      },
    };
  };
  return { split, put, def, min, max, dragging, bodyRef: bodyRef as RefObject<HTMLDivElement>, gripProps };
}
